/**
 * The frozen result of a closing competition — what `closeCompetition`
 * (hooks/competition/useCompetitionAdmin.js) writes to `results/{groupId}` and to
 * the competition's `groupNames`, computed from the raw groups, sessions,
 * registrations and attempts.
 *
 * Its own pure module for two readers: the close flow, and the E2E seed's closed
 * competition (tests/e2e/seed/competitions.js), which must freeze its results the
 * way a real close does rather than carry a hand-written copy of the rows.
 *
 * `results` holds one ranking per group and, for every group that has finals, a
 * `${groupId}_finals` ranking over the finalists' finals-session attempts only.
 * Every row is `composeGroupRanking`'s, plus `displayName` (empty-string fallback,
 * not "?"), `startNumber` and `isGuest` — the frozen format has carried those three
 * since it existed. Excluded-from-ranking competitors stay in with their would-be
 * rank so the closed-comp dashboard and PDF can still show them.
 */
import { composeGroupRanking } from "@sendly/shared";
// Explicit extension: the E2E seed loads this module in plain Node ESM.
import { getFinalsGroupsInfo } from "./finals.js";

/**
 * @param {{ settings: object, groups: object[], sessions: object[],
 *   registrations: object[], attempts: object[] }} parts  each element with its `id`
 * @returns {{ results: Record<string, object[]>, groupNames: Record<string, string> }}
 */
export function buildCloseSnapshot({ settings, groups, sessions, registrations, attempts }) {
  const excludedUids = new Set(
    registrations.filter(r => r.role === "competitor" && r.excludeFromRanking === true).map(r => r.uid)
  );
  const decorate = (ranking, groupRegs) => ranking.map(r => {
    const reg = groupRegs.find(x => x.uid === r.uid);
    return { ...r, displayName: reg?.displayName || "", startNumber: reg?.startNumber || null, ...(reg?.isGuest ? { isGuest: true } : {}) };
  });

  const results = {};
  for (const g of groups) {
    const groupRegs = registrations.filter(r => r.groupId === g.id && r.role === "competitor");
    const ranking = composeGroupRanking({
      settings,
      routePointsGroupId: g.id,
      competitorUids: groupRegs.map(r => r.uid),
      attempts: attempts.filter(a => a.groupId === g.id),
      // Every registration, because a proxy guest's fee is paid by their
      // parent, who may sit in another group entirely.
      payerRegs: registrations,
      nameRegs: groupRegs,
      excludedUids,
    });
    results[g.id] = decorate(ranking, groupRegs);
  }

  // Finals — a separate ranking for each group with a finals session.
  const finalsInfo = getFinalsGroupsInfo(groups, sessions);
  const finalsSessionIds = new Set(sessions.filter(s => s.type === "finals").map(s => s.id));
  for (const fi of finalsInfo) {
    const ranking = composeGroupRanking({
      settings,
      routePointsGroupId: fi.groupId,
      competitorUids: fi.finalistUids || [],
      attempts: attempts.filter(a => a.groupId === fi.groupId && finalsSessionIds.has(a.sessionId)),
      payerRegs: registrations,
      nameRegs: registrations.filter(r => r.groupId === fi.groupId),
      excludedUids,
    });
    const groupRegs = registrations.filter(r => r.groupId === fi.groupId && r.role === "competitor");
    results[`${fi.groupId}_finals`] = decorate(ranking, groupRegs);
  }

  // Group names for the export: a closed competition's groups subcollection is
  // not loaded any more.
  const groupNames = {};
  for (const g of groups) groupNames[g.id] = g.name || g.id;
  for (const fi of finalsInfo) {
    groupNames[`${fi.groupId}_finals`] = fi.finalsSessionName
      ? `${fi.groupName} - ${fi.finalsSessionName}`
      : `${fi.groupName} - FINALS`;
  }
  return { results, groupNames };
}
